import axios from "axios";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  Text,
  Heading,
  VStack,
  Container,
  Stack,
} from "@chakra-ui/react";

export default function RequestQuotes() {
  const colors = {
    teal: "#1da179",
    tealLight: "#34c49b",
    grayBg: "#f9f9f9",
  };

  const toast = useToast();
  const [captchaToken, setCaptchaToken] = useState("");
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await axios.post(
        "https://actpolymers-spring-26d90d713ec5.herokuapp.com/api/contact",
        {
          ...data,
          captchaToken,
        }
      );

      toast({
        title: isArabic ? "تم الإرسال بنجاح" : "Request Submitted",
        description: isArabic
          ? "سنتواصل معك قريبًا بخصوص عرض السعر."
          : "We'll get back to you soon with a quote.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      reset();
      setCaptchaToken("");
    } catch (error) {
      toast({
        title: isArabic ? "فشل الإرسال" : "Submission Failed",
        description: isArabic
          ? "حدث خطأ أثناء الإرسال، حاول مرة أخرى لاحقًا."
          : "Please try again later.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg={colors.grayBg} py={16} px={4}>
      <Container maxW="4xl">
        {/* Section Header */}
        <Stack spacing={3} textAlign="center" mb={10}>
        <Heading
  fontSize={{ base: "2xl", md: "3xl" }}
  fontWeight="extrabold"
  lineHeight="1.4"
  pb={1}
  bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
  bgClip="text"
>
  {isArabic ? "طلب عرض سعر" : "Request a Quote"}
</Heading>

          <Text fontSize="md" color="gray.600" maxW="2xl" mx="auto">
            {isArabic
              ? "املأ النموذج أدناه وسنعود إليك بعرض سعر مخصص لاحتياجاتك."
              : "Fill out the form below and we’ll get back to you with a tailored quote."}
          </Text>
        </Stack>

        {/* Form Card */}
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 6, md: 10 }}
          boxShadow="2xl"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={5} align="stretch">
              {/* Name */}
              <FormControl isInvalid={errors.name}>
                <FormLabel>{isArabic ? "الاسم" : "Name"}</FormLabel>
                <Input
                  {...register("name", { required: "Name is required" })}
                  placeholder={isArabic ? "اسمك" : "Your Name"}
                  size="lg"
                  borderRadius="md"
                  focusBorderColor={colors.teal}
                />
                {errors.name && (
                  <Text color="red.500" fontSize="xs">
                    {errors.name.message}
                  </Text>
                )}
              </FormControl>

              {/* Phone */}
              <FormControl isInvalid={errors.phone}>
                <FormLabel>{isArabic ? "رقم الجوال" : "Phone Number"}</FormLabel>
                <Input
                  {...register("phone", {
                    required: "Phone number is required",
                  })}
                  placeholder={isArabic ? "رقم الجوال" : "Your Phone"}
                  size="lg"
                  borderRadius="md"
                  focusBorderColor={colors.teal}
                />
                {errors.phone && (
                  <Text color="red.500" fontSize="xs">
                    {errors.phone.message}
                  </Text>
                )}
              </FormControl>

              {/* Email */}
              <FormControl isInvalid={errors.email}>
                <FormLabel>{isArabic ? "البريد الإلكتروني" : "Email"}</FormLabel>
                <Input
                  {...register("email", { required: "Email is required" })}
                  type="email"
                  placeholder="name@example.com"
                  size="lg"
                  borderRadius="md"
                  focusBorderColor={colors.teal}
                />
                {errors.email && (
                  <Text color="red.500" fontSize="xs">
                    {errors.email.message}
                  </Text>
                )}
              </FormControl>

              {/* Product Name */}
              <FormControl>
                <FormLabel>
                  {isArabic ? "اسم المنتج" : "Product Name"}
                </FormLabel>
                <Input
                  {...register("productName")}
                  placeholder={
                    isArabic
                      ? "اسم المنتج (اختياري)"
                      : "Product Name (Optional)"
                  }
                  size="lg"
                  borderRadius="md"
                  focusBorderColor={colors.teal}
                />
              </FormControl>

              {/* Color Info */}
              <FormControl>
                <FormLabel>
                  {isArabic ? "اللون أو الكود" : "Color Name or Code"}
                </FormLabel>
                <Input
                  {...register("colorInfo")}
                  placeholder={
                    isArabic ? "أدخل اللون أو الكود" : "Enter Color or Code"
                  }
                  size="lg"
                  borderRadius="md"
                  focusBorderColor={colors.teal}
                />
              </FormControl>

              {/* Message */}
              <FormControl isInvalid={errors.message}>
                <FormLabel>
                  {isArabic ? "تفاصيل إضافية" : "Additional Details"}
                </FormLabel>
                <Textarea
                  {...register("message", { required: "Message is required" })}
                  placeholder={
                    isArabic
                      ? "اكتب التفاصيل الخاصة بطلبك..."
                      : "Write your request details..."
                  }
                  size="lg"
                  borderRadius="md"
                  rows={5}
                  focusBorderColor={colors.teal}
                />
                {errors.message && (
                  <Text color="red.500" fontSize="xs">
                    {errors.message.message}
                  </Text>
                )}
              </FormControl>

              {/* CAPTCHA */}
              {/* <Box mt={2} w="full" textAlign="center">
                <ReCAPTCHA
                  sitekey="6Ldat88rAAAAANDbsRBRqgBCkoAggJBLneI1xVR2"
                  onChange={(token) => setCaptchaToken(token)}
                />
              </Box> */}

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                borderRadius="full"
                px={10}
                py={6}
                fontWeight="bold"
                bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
                color="white"
                isLoading={isSubmitting}
                width="full"
              >
                {isArabic ? "إرسال الطلب" : "Submit Request"}
              </Button>
            </VStack>
          </form>
        </Box>
      </Container>
    </Box>
  );
}
